import { useReviewPeriodLock } from '../workspace/reviewPeriod';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { DOCUMENT_PROCESSING_PATH, facilityWorkspacePath } from '../routes';
import { CollectionsDrawer } from '../collections/CollectionsExperience';
import { WorkspaceGuide, WorkspaceDisclosure } from '../components/WorkspaceExperience';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronRight, Download, FileText, FolderOpen, RefreshCw, Upload, X } from 'lucide-react';
import { apiErrorMessage, downloadFile, getFacilityDocuments, uploadFacilityDocument, withdrawFacilityDocument } from '../api/abfApi';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import type { FacilityDocument, FacilityDocuments } from '../types/facilityWorkspace';
import { fmtDate, fmtDateTime, plural } from '../presentation/format';

/**
 * WS4 — the facility's document repository (Oaktree row 6.5). A registry, not a copy:
 * every file a workflow holds is listed from that workflow's record with its hash and
 * what it produced. Free uploads (side letters, opinions, correspondence) are stored,
 * hashed and attributed, and drive nothing.
 */

interface Props {
  facilityId: number;
  userRole?: string | null;
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const KIND_LABELS: Record<string, string> = {
  CREDIT_AGREEMENT: 'Credit agreement', AMENDMENT: 'Amendment', TAPE: 'Servicer tape', BBC: 'Borrowing base certificate',
  COMPLIANCE_CERT: 'Compliance certificate', SERVICER_REPORT: 'Servicer report', PAYING_AGENT_REPORT: 'Paying agent report',
  BANK_STATEMENT: 'Bank statement', EVIDENCE_PACK: 'Evidence pack', CERTIFIED_REPORT: 'Certified report', PERIOD_INTAKE: 'Period intake',
  OTHER: 'Other', SIDE_LETTER: 'Side letter', LEGAL_OPINION: 'Legal opinion', CORRESPONDENCE: 'Correspondence', WAIVER_LETTER: 'Waiver letter',
  CLOSING_MEMO: 'Closing memo', KYC: 'KYC', INSURANCE: 'Insurance', VALUATION_SUPPORT: 'Valuation support',
};
const READ_ONLY_ROLES = new Set(['COMPLIANCE', 'COMPLIANCE_OFFICER', 'CLIENT', 'USER']);

const Shell = styled.section`
  margin-top: .85rem; padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h3 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  .toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-end; margin-top: .8rem; }
  .toolbar label { display: flex; flex-direction: column; gap: 4px; color: #94a8c2; font-size: 11px; }
  select, input[type=text] { min-height: 36px; padding: .4rem .55rem; border: 1px solid rgba(148,163,184,.2); border-radius: 9px; background: #07101f; color: #e2e8f0; font-size: 13px; }
  button { display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: .4rem .7rem; border: 1px solid rgba(148,163,184,.24); border-radius: 8px; color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 13px; }
  button:disabled { opacity: .5; cursor: default; }
  button.primary { color: #e0f2fe; border-color: #7dd3fc; background: rgba(14,116,144,.25); }
  table { width: 100%; border-collapse: collapse; margin-top: .9rem; font-size: 12px; }
  th { text-align: left; padding: .5rem .55rem; color: #7dd3fc; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; font-size: 11px; border-bottom: 1px solid rgba(148,163,184,.2); }
  td { padding: 16px 12px; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; vertical-align: top; }
  a { color: #a6d8f5; font-size: 13px; }
  tbody tr:not(.group):hover { background: rgba(56,189,248,.04); }
  td { overflow-wrap: anywhere; }
  table { table-layout: fixed; }
  th:first-child { width: 27%; } th:nth-child(2) { width: 10%; } th:nth-child(3) { width: 29%; } th:nth-child(4) { width: 16%; }
  td.hash { font-family: ui-monospace, monospace; color: #94a3b8; font-size: 11px; }
  .muted { color: #94a3b8; }
  .chip { display: inline-block; padding: .1rem .45rem; border-radius: 999px; font-size: 11px; font-weight: 650; border: 1px solid rgba(148,163,184,.3); color: #cbd5e1; }
  .chip.official { color: #6ee7b7; border-color: rgba(110,231,183,.4); }
  .chip.withdrawn { color: #fda4af; border-color: rgba(253,164,175,.4); text-decoration: line-through; }
  .boundary { margin-top: .7rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
  tr.group td { padding: .55rem .55rem; border-top: 1px solid rgba(148,163,184,.2); background: rgba(15,23,42,.55); color: #e2e8f0; font-weight: 650; }
  tr.group button { min-height: 0; padding: 0; border: 0; background: transparent; color: inherit; font-weight: 650; }
  tr.group .count { margin-left: 8px; color: #94a3b8; font-weight: 500; }
  .filepick { position: relative; display: inline-flex; align-items: center; gap: 8px; }
  .filepick input[type=file] { position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; clip: rect(0 0 0 0); }
  .filepick .name { max-width: 16rem; overflow: hidden; color: #cbd5e1; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
  .filepick .name.empty { color: #64748b; }
`;

const when = (iso: string | null) => fmtDateTime(iso);
const FACILITY_LEVEL = 'Facility-level';
const bytes = (n?: number) => n == null ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`;

/** Fix list 1 (row 6.5): where the file is kept, in words; a locked copy names its retention date. */
const keptLabel = (d: FacilityDocument) => {
  const s = d.storage;
  if (!s) return d.sourceTable === 'abf_bank_cash_file'
    ? <span className="muted" title="Parsed lines and the file hash are kept; the file itself is not">lines + hash</span>
    : <span className="muted">—</span>;
  const where = s.provider === 'S3' ? 'S3' : 'Local disk';
  const detail = s.lockedUntil ? `locked to ${fmtDate(s.lockedUntil)}` : s.versioned ? 'versioned' : '';
  return <span className={`chip ${s.lockedUntil ? 'official' : ''}`} title={`Stored ${fmtDateTime(s.storedAt)}`}>{where}{detail ? ` · ${detail}` : ''}</span>;
};

const DocumentsWorkspace: React.FC<Props> = ({ facilityId, userRole, onFeedback }) => {
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const [allPeriods, setAllPeriods] = useState(false);
  const [includeFacility, setIncludeFacility] = useState(true);
  const [selected, setSelected] = useState<FacilityDocument | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [model, setModel] = useState<FacilityDocuments | null>(null);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('');
  const [authorityFilter, setAuthorityFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [uploadKind, setUploadKind] = useState('OTHER');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const [busy, setBusy] = useState(false);
  useReviewPeriodLock(busy);
  const [fileName, setFileName] = useState('');
  // Which period groups are open; the newest is open by default, the rest fold until asked.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const canUpload = !READ_ONLY_ROLES.has(String(userRole || '').toUpperCase());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setModel(await getFacilityDocuments(facilityId));
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The document register could not be loaded.') });
    } finally {
      setLoading(false);
    }
  }, [facilityId, onFeedback]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const requested = Number(params.get('period') || params.get('periodId'));
    const label = model?.periods?.find(p => Number(p.id) === requested)?.period_label;
    if (label) { setPeriodFilter(label); setOpenGroups({[label]:true}); }
  }, [model?.periods, params]);

  const documents = useMemo(() => model?.documents ?? [], [model]);
  const kinds = useMemo(() => Array.from(new Set(documents.map(d => d.kind))).sort(), [documents]);
  const periods = useMemo(() => Array.from(new Set(documents.map(d => d.periodLabel).filter((p): p is string => !!p))).sort().reverse(), [documents]);
  const visible = useMemo(() => documents.filter(d => `${d.title} ${d.fileName || ''} ${d.produced} ${d.receivedBy || ''} ${d.contentHash || ''}`.toLowerCase().includes(search.trim().toLowerCase()) && (!kindFilter || d.kind === kindFilter) && (!authorityFilter || d.authority === authorityFilter) && (allPeriods || !periodFilter || d.periodLabel === periodFilter || (includeFacility && !d.periodLabel))), [documents, kindFilter, authorityFilter, periodFilter, search, allPeriods, includeFacility]);
  // Every workflow file lands within minutes of the run that produced it, so "received" says little;
  // the period a document belongs to is how a reader looks for it. Newest period first, facility-level last.
  const groups = useMemo(() => {
    const byPeriod = new Map<string, FacilityDocument[]>();
    visible.forEach(d => {
      const key = d.periodLabel || FACILITY_LEVEL;
      const list = byPeriod.get(key) || [];
      list.push(d);
      byPeriod.set(key, list);
    });
    const labels = Array.from(byPeriod.keys()).filter(k => k !== FACILITY_LEVEL).sort().reverse();
    if (byPeriod.has(FACILITY_LEVEL)) labels.push(FACILITY_LEVEL);
    return labels.map(label => ({ label, documents: byPeriod.get(label) || [] }));
  }, [visible]);
  const isOpen = (label: string, index: number) => openGroups[label] ?? (Boolean(search) || index === 0 || groups.length === 1);
  const toggleGroup = (label: string, index: number) => setOpenGroups(prev => ({ ...prev, [label]: !isOpen(label, index) }));

  useEffect(() => {
    let current = true; let url: string | null = null;
    setPreview(null); setPreviewError('');
    if (selected?.download && /\.pdf$/i.test(selected.download.fileName)) {
      void axios.get(selected.download.url, { responseType: 'blob' }).then(response => {
        url = URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}));
        if (current) setPreview(url); else URL.revokeObjectURL(url);
      }).catch(error => { if (current) setPreviewError(apiErrorMessage(error, 'The PDF preview could not be loaded.')); });
    }
    return () => { current = false; if (url) URL.revokeObjectURL(url); };
  }, [selected]);
  const workflowFor = (d: FacilityDocument) => ['CREDIT_AGREEMENT','AMENDMENT'].includes(d.kind) ? 'contract'
    : ['TAPE','BBC'].includes(d.kind) ? 'collateral'
    : ['SERVICER_REPORT','PAYING_AGENT_REPORT','BANK_STATEMENT','PERIOD_INTAKE'].includes(d.kind) ? 'collections'
    : d.kind === 'CERTIFIED_REPORT' ? 'reporting' : d.kind === 'COMPLIANCE_CERT' ? 'monitoring' : null;
  const workflowHref = (d: FacilityDocument) => {
    const workspace = workflowFor(d);
    if (!workspace) return null;
    const period = model?.periods?.find(p => p.period_label === d.periodLabel);
    const query = new URLSearchParams();
    if (period) query.set('period', String(period.id));
    if (d.sourceTable === 'abf_facility_version' && d.sourceId) query.set('version', d.sourceId);
    if (d.sourceTable === 'abf_servicer_tape_ingest' && d.sourceId) query.set('tape', d.sourceId);
    return facilityWorkspacePath(facilityId, workspace) + (query.size ? `?${query}` : '');
  };

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { onFeedback({ tone: 'error', message: 'Choose a file first.' }); return; }
    setBusy(true);
    try {
      const r = await uploadFacilityDocument(facilityId, file, { kind: uploadKind, title: uploadTitle || undefined, note: uploadNote || undefined });
      onFeedback({ tone: 'success', message: `${r.fileName} stored (${bytes(r.byteSize)}, SHA-256 ${r.contentHash.slice(0, 12)}…). Nothing was extracted from it.` });
      if (fileRef.current) fileRef.current.value = '';
      setFileName(''); setUploadTitle(''); setUploadNote('');
      await load();
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The document could not be stored.') });
    } finally {
      setBusy(false);
    }
  };
  const withdraw = async (d: FacilityDocument) => {
    const reason = window.prompt(`Withdraw "${d.title}"? Give the reason (recorded with your name).`);
    if (!reason) return;
    try {
      await withdrawFacilityDocument(facilityId, Number(d.sourceId), reason);
      onFeedback({ tone: 'success', message: `${d.title} withdrawn.` });
      await load();
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The document could not be withdrawn.') });
    }
  };
  const download = async (d: FacilityDocument) => {
    if (!d.download) return;
    try {
      await downloadFile(d.download.url, d.download.fileName);
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The file could not be downloaded.') });
    }
  };

  return (
    <Shell aria-label="Facility documents" aria-busy={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3><FileText size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Documents</h3>
          <p className="sub">{documents.length} on record · every workflow file with its hash and what it produced, plus documents stored here directly.</p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} />Refresh</button>
      </div>

      <WorkspaceGuide><h2>Find the evidence behind a decision</h2><p>Search files and their outputs, inspect the source and retention details, then open the workflow that uses them.</p><div className="actions"><Link to={DOCUMENT_PROCESSING_PATH}>Process a document →</Link><Link to={facilityWorkspacePath(facilityId, 'collections') + `?${params}`}>Open period evidence intake →</Link></div><p>Processing extracts fields for review and import. Attach supporting evidence below when you only need a stored file. Confirm the facility, period and account in the intake workflow before importing.</p></WorkspaceGuide>
      <div className="toolbar">
        <label style={{flex: 1}}>Search evidence<input type="text" aria-label="Search evidence" placeholder="File, output, person or hash…" value={search} onChange={e => setSearch(e.target.value)} /></label>
        <label>Kind<select aria-label="Filter by kind" value={kindFilter} onChange={e => setKindFilter(e.target.value)}><option value="">All kinds</option>{kinds.map(k => <option key={k} value={k}>{KIND_LABELS[k] || k}</option>)}</select></label>
        <label>Authority<select aria-label="Filter by authority" value={authorityFilter} onChange={e => setAuthorityFilter(e.target.value)}><option value="">All</option>{['OFFICIAL', 'REPORTED', 'EXTRACTED', 'DERIVED', 'RECORDED', 'WITHDRAWN'].map(a => <option key={a} value={a}>{a.toLowerCase()}</option>)}</select></label>
        <span className="muted">{allPeriods ? 'Searching all periods' : `Review period ${periodFilter || 'not selected'}`}</span>
        <label><span><input type="checkbox" checked={allPeriods} onChange={e => setAllPeriods(e.target.checked)} /> Search all periods</span></label>
        <label><span><input type="checkbox" checked={includeFacility} onChange={e => setIncludeFacility(e.target.checked)} /> Include facility-level documents</span></label>
        {(search || kindFilter || authorityFilter || allPeriods) && <button type="button" onClick={() => { setSearch(''); setKindFilter(''); setAuthorityFilter(''); setAllPeriods(false); }}><X size={14} />Clear</button>}
      </div>

      {canUpload && (
        <WorkspaceDisclosure><summary>Attach supporting evidence <small>Store a file without extracting or importing data</small></summary><div className="toolbar" style={{ padding: '.7rem', border: '1px dashed rgba(148,163,184,.3)', borderRadius: 10 }}>
          <label>File
            <span className="filepick">
              <input ref={fileRef} id={`abf-document-file-${facilityId}`} type="file" aria-label="Document file" onChange={e => setFileName(e.target.files?.[0]?.name || '')} />
              <button type="button" onClick={() => fileRef.current?.click()}><FolderOpen size={14} />Choose a file</button>
              <span className={`name ${fileName ? '' : 'empty'}`} title={fileName || undefined}>{fileName || 'No file chosen'}</span>
            </span>
          </label>
          <label>Kind<select aria-label="Document kind" value={uploadKind} onChange={e => setUploadKind(e.target.value)}>{(model?.uploadKinds ?? ['OTHER']).map(k => <option key={k} value={k}>{KIND_LABELS[k] || k}</option>)}</select></label>
          <label>Title<input type="text" aria-label="Document title" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Defaults to the file name" /></label>
          <label>Note<input type="text" aria-label="Document note" value={uploadNote} onChange={e => setUploadNote(e.target.value)} placeholder="Optional" style={{ minWidth: 220 }} /></label>
          <button type="button" className="primary" disabled={busy} onClick={() => void upload()}><Upload size={14} />{busy ? 'Storing…' : 'Store document'}</button>
          <span className="muted" style={{ fontSize: 11 }}>Stored and hashed only; nothing is extracted. Agreements, tapes and statements go through their own workflows.</span>
        </div></WorkspaceDisclosure>
      )}

      <table>
        <thead><tr><th>Document</th><th>Status</th><th>Used for</th><th>Received</th><th /></tr></thead>
        <tbody>
          {groups.map((group, index) => <React.Fragment key={group.label}>
            <tr className="group">
              <td colSpan={5}>
                <button type="button" aria-expanded={isOpen(group.label, index)} onClick={() => toggleGroup(group.label, index)}>
                  {isOpen(group.label, index) ? <ChevronDown size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <ChevronRight size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                  {group.label === FACILITY_LEVEL ? 'Facility-level' : `Period ${group.label}`}
                  <span className="count">{plural(group.documents.length, 'document')}</span>
                </button>
              </td>
            </tr>
            {isOpen(group.label, index) && group.documents.map((d, i) => (
            <tr key={`${d.sourceTable}-${d.sourceId}-${i}`}>
              <td><strong>{d.title}</strong><div className="muted" style={{marginTop:6}}>{KIND_LABELS[d.kind] || d.kind}{d.byteSize != null ? ` · ${bytes(d.byteSize)}` : ''}</div></td>
              <td><span className={`chip ${d.authority === 'OFFICIAL' ? 'official' : d.authority === 'WITHDRAWN' ? 'withdrawn' : ''}`}>{d.authority.toLowerCase()}</span></td>
              <td className="muted">{d.produced || 'Supporting evidence'}</td>
              <td>{when(d.receivedAt)}<div className="muted">{d.receivedBy || 'Actor not recorded'}</div></td>
              <td><button type="button" onClick={() => setSelected(d)}>Review evidence <ChevronRight size={14} /></button></td>
            </tr>
            ))}
          </React.Fragment>)}
          {!visible.length && !loading && <tr><td colSpan={5} className="muted" style={{ padding: '1.2rem', textAlign: 'center' }}>No document matches these filters.</td></tr>}
        </tbody>
      </table>
      {selected && <CollectionsDrawer title={selected.title} onClose={() => setSelected(null)} width={960}>
        <WorkspaceGuide><h2>{KIND_LABELS[selected.kind] || selected.kind} · {selected.periodLabel || 'Facility-level'}</h2><p>{selected.produced || 'Supporting evidence'}</p><p>Received {when(selected.receivedAt)} · {selected.receivedBy || 'Actor not recorded'}</p><p>Authority: {selected.authority.toLowerCase()}</p></WorkspaceGuide>
        <div className="toolbar">{selected.download && <button type="button" onClick={() => void download(selected)}><Download size={14} />Download original</button>}{workflowHref(selected) && <Link to={workflowHref(selected)!}>Open source workflow →</Link>}{canUpload && selected.sourceTable === 'abf_facility_document' && !selected.withdrawn && <button type="button" onClick={() => { void withdraw(selected); setSelected(null); }}>Withdraw with reason</button>}</div>
        {previewError && <p role="alert">{previewError}</p>}
        {preview && <iframe title="Evidence PDF preview" src={preview} style={{width:'100%',height:'60vh',border:0,marginTop:16}} />}
        <WorkspaceDisclosure open><summary>Source, integrity & retention</summary><p>Source record: {selected.sourceTable} #{selected.sourceId}</p><p style={{overflowWrap:'anywhere'}}>SHA-256: {selected.contentHash || 'Not recorded'}</p><p>{keptLabel(selected)}</p>{selected.note && <p>{selected.note}</p>}{!selected.download && <p>The original file is not available from this register. Use the source workflow to inspect its retained evidence.</p>}</WorkspaceDisclosure>
        <WorkspaceDisclosure><summary>Related files in this period</summary><p>Files of the same kind and period are listed below; their source records determine whether they replace one another.</p>{documents.filter(d => d.kind === selected.kind && d.periodLabel === selected.periodLabel).map((d,i) => <p key={`${d.sourceTable}-${d.sourceId}-${i}`}><button type="button" onClick={() => setSelected(d)}>{d.title} · {d.authority.toLowerCase()}</button></p>)}</WorkspaceDisclosure>
      </CollectionsDrawer>}
      {model?.authorityBoundary && <div className="boundary">{model.authorityBoundary}</div>}
    </Shell>
  );
};

export default DocumentsWorkspace;
